import type { ScreenId } from '@galapagos/shared';

/** Historical analytics identities retained after their active screens retire. */
export const retiredOnboardingScreenIds = [
  'onboarding-summary',
] as const satisfies readonly ScreenId[];

/**
 * Onboarding step_key → registered ScreenId, for screen-view analytics.
 *
 * Route paths surface the flow-local step key (the `[step]` segment), so the
 * shared screen identity is derived from it here: exact entries for the
 * single-step screens, prefix rules for the screen archetypes that are
 * authored once per substance variant (`weekly_drinks[_x]`, `week_summary[_x]`,
 * `typical_drinks[_x]`, `summary[_x]`), the per-program goal variants
 * (`goal[_x]`), and the TAPS 2 question steps (`taps2_*`), which all collapse
 * onto their shared registered screen id.
 */
const onboardingScreenIds: Readonly<Record<string, ScreenId>> = {
  start: 'onboarding-start',
  name: 'onboarding-name',
  motivation: 'onboarding-motivation',
  substance: 'onboarding-substance',
  condition: 'onboarding-condition',
  goal: 'onboarding-goal',
  change_history: 'onboarding-change-history',
  cutback_degree: 'onboarding-cutback-degree',
  sona_intro: 'onboarding-sona-intro',
  rewards_intro: 'onboarding-rewards-intro',
  conversation_mode: 'onboarding-conversation-mode',
};

export function onboardingScreenId(stepKey: string): ScreenId | null {
  const exact = onboardingScreenIds[stepKey];
  if (exact) return exact;
  if (stepKey === 'weekly_drinks' || stepKey.startsWith('weekly_drinks_'))
    return 'onboarding-weekly-usage';
  if (stepKey === 'week_summary' || stepKey.startsWith('week_summary_'))
    return 'onboarding-week-summary';
  if (stepKey === 'typical_drinks' || stepKey.startsWith('typical_drinks_'))
    return 'onboarding-typical-usage';
  // Retain historical identity for old route/telemetry records; active flows omit the recap.
  if (stepKey === 'summary' || stepKey.startsWith('summary_')) return 'onboarding-summary';
  if (stepKey.startsWith('goal_')) return 'onboarding-goal';
  if (stepKey.startsWith('taps2_')) return 'onboarding-taps2';
  return null;
}
